import { patchQmr, qmrActions, saveQmrCvtChallengeDetails, useQmrDispatch, useQmrState } from '@packages/contexts/qmrs';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { CvtExceptionStatus, CvtReason, QmrStatusId, UpdateQmrCvtChallengeDetailsDto } from '@packages/models/api';
import { TextareaHelper, Typography } from '@packages/ui/shared';
import Select, { SelectOption } from '@web/components/select';
import { httpClient, qmrsService } from '@web/services/singletons';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';

const logger = getLogger('qmr');

const SAVE_DELAY_MS = 5000;

const labelStyle = { fontWeight: 400 } as const;
const textInputStyle = { fontSize: 15, fontWeight: 400 as const };

type CvtDiagnosticChallengeDetailsProps = {
    isDisabled?: boolean;
    isUpdatingDiagnostic?: boolean;
};

/**
 * The CVT challenge on the report.
 *
 * Renders the checkbox on a draft or `/edit`, with a Reason and Description once the recommendation is challenged,
 * and the saved challenge as text otherwise.
 */
export function CvtDiagnosticChallengeDetails({
    isDisabled = false,
    isUpdatingDiagnostic = false,
}: CvtDiagnosticChallengeDetailsProps) {
    const { t } = useTranslation();
    const { localPatch, qmr } = useQmrState();
    const qmrDispatch = useQmrDispatch();
    const { abortSignalRef } = useAbortController();
    const editRouteMatch = useMatch(`/${qmr?.isNqr ? 'nqrs' : 'qmrs'}/:displayIdentifier/edit`);

    const [reasonOptions, setReasonOptions] = useState<SelectOption[]>([]);

    const [description, setDescription] = useState<string>(qmr?.cvtChallengeDetails?.cvtChallengeExplanation ?? '');
    const [descriptionError, setDescriptionError] = useState<string>('');
    const [reasonError, setReasonError] = useState<string>('');

    const isDraft = qmr?.qmrStatus.qmrStatusId === QmrStatusId.Draft;
    const isEditable = isDraft || Boolean(editRouteMatch);
    const reportType = qmr?.isNqr ? 'NQR' : 'QMR';

    const cvtChallengeDetails = qmr?.cvtChallengeDetails;
    const hasAttachedDiagnostic = Boolean(qmr?.cvtDiagnosticsJobId);

    const applicableExceptionStatus = hasAttachedDiagnostic
        ? CvtExceptionStatus.RecommendationChallenged
        : CvtExceptionStatus.DiagnosticUnavailable;

    const applicableExceptionStatusLabel = hasAttachedDiagnostic
        ? t('qmr:sections.failCode.cvtDiagnostic.challenge.recommendation.editable')
        : t('qmr:sections.failCode.cvtDiagnostic.challenge.unavailable.editable');

    const isExceptionStatusChecked = cvtChallengeDetails?.cvtExceptionStatus === applicableExceptionStatus;
    const isRecommendationChallenged = isExceptionStatusChecked && hasAttachedDiagnostic;

    const isRecommendationChallengeSubmitted =
        cvtChallengeDetails?.cvtExceptionStatus === CvtExceptionStatus.RecommendationChallenged;

    const submittedExceptionStatusLabel = isRecommendationChallengeSubmitted
        ? t('qmr:sections.failCode.cvtDiagnostic.challenge.recommendation.readOnly')
        : t('qmr:sections.failCode.cvtDiagnostic.challenge.unavailable.readOnly');

    const reasonOptionsWithPlaceholder = [
        { title: t('qmr:sections.failCode.cvtDiagnostic.challenge.reason.placeholder'), value: '' },
        ...reasonOptions,
    ];

    const currentReason = cvtChallengeDetails?.cvtChallengeReason || null;
    const hasDescriptionChanged = description !== (cvtChallengeDetails?.cvtChallengeExplanation ?? '');
    const isChallengeDeletionHeld = localPatch?.cvtChallengeDetails === null;
    const hasChanges = localPatch?.cvtChallengeDetails !== undefined || hasDescriptionChanged;

    function handleExceptionStatusCheckboxChange() {
        if (!qmr?.qmrId) {
            return;
        }

        setReasonError('');
        setDescriptionError('');

        if (isExceptionStatusChecked) {
            patchQmr({
                qmrId: qmr.qmrId,
                qmrsService,
                qmrDispatch,
                isLocalPatch: true,
                qmrPatch: { cvtChallengeDetails: null },
            });
            return;
        }

        setDescription('');
        holdChanges({
            cvtExceptionStatus: applicableExceptionStatus,
            cvtChallengeReason: null,
            cvtChallengeExplanation: null,
        });
    }

    function handleReasonSelectChange(event: FormEvent<HTMLSelectElement>) {
        const reason = event.currentTarget.value;
        const hasReason = Boolean(reason);

        setReasonError(
            hasReason ? '' : t('qmr:sections.failCode.cvtDiagnostic.challenge.reason.errorMessage', { reportType })
        );
        holdChanges({ cvtChallengeReason: reason || null });
    }

    function handleDescriptionBlur() {
        if (!hasDescriptionChanged) {
            return;
        }

        holdChanges({ cvtChallengeExplanation: description || null });
    }

    /**
     * Sets the Description and its error, and holds it at once when it goes blank. A Description of only spaces shows
     * the error but is saved as typed, as concern and correction save theirs.
     */
    function handleDescriptionTextChange(value: string) {
        const hasDescription = Boolean(value.trim());

        httpClient.refreshToken();
        setDescription(value);
        setDescriptionError(
            hasDescription
                ? ''
                : t('qmr:sections.failCode.cvtDiagnostic.challenge.description.errorMessage', { reportType })
        );

        if (hasDescription) {
            return;
        }

        holdChanges({ cvtChallengeExplanation: value || null });
    }

    /**
     * Holds the challenge in `localPatch` and returns it. A field not given keeps its current value: the status and
     * Reason from the report, the Description as typed. All three are always held, since the backend clears a field
     * left out.
     */
    const holdChanges = useCallback(
        (changedCvtChallengeDetails: Partial<UpdateQmrCvtChallengeDetailsDto>) => {
            const {
                cvtChallengeExplanation = description || null,
                cvtChallengeReason = currentReason,
                cvtExceptionStatus = cvtChallengeDetails?.cvtExceptionStatus,
            } = changedCvtChallengeDetails;

            if (!qmr?.qmrId || !cvtExceptionStatus) {
                return;
            }

            const updatedCvtChallengeDetails = { cvtExceptionStatus, cvtChallengeReason, cvtChallengeExplanation };

            patchQmr({
                qmrId: qmr.qmrId,
                qmrsService,
                qmrDispatch,
                isLocalPatch: true,
                qmrPatch: { cvtChallengeDetails: updatedCvtChallengeDetails },
            });

            return updatedCvtChallengeDetails;
        },
        [currentReason, cvtChallengeDetails?.cvtExceptionStatus, description, qmr?.qmrId, qmrDispatch]
    );

    const reasonTitle = useMemo(
        () => reasonOptions.find((option) => option.value === cvtChallengeDetails?.cvtChallengeReason)?.title,
        [cvtChallengeDetails?.cvtChallengeReason, reasonOptions]
    );

    /**
     * Saves the challenge `SAVE_DELAY_MS` after the last change, holding the typed Description first, so a burst of
     * changes goes out as one request. Only a draft saves here; on `/edit`, Save Changes sends what is held. Nothing
     * is sent while no change is held and the Description is as saved. It waits while the report's diagnostic is being
     * updated, since the update deletes the challenge. It counts its saves while they're out, so an update waits for
     * every one.
     */
    useEffect(() => {
        if (!isEditable || !hasChanges || isUpdatingDiagnostic) {
            return;
        }

        const timeoutId = setTimeout(() => {
            if (!qmr?.qmrId) {
                return;
            }

            const updatedCvtChallengeDetails = isChallengeDeletionHeld ? null : holdChanges({});

            if (!isDraft) {
                return;
            }

            if (updatedCvtChallengeDetails === undefined) {
                return;
            }

            qmrDispatch(qmrActions.startCvtChallengeDetailsSave());

            saveQmrCvtChallengeDetails({
                qmrId: qmr.qmrId,
                cvtChallengeDetails: updatedCvtChallengeDetails,
                qmrsService,
                qmrDispatch,
                signal: abortSignalRef.current,
            })
                .catch((e) => {
                    alert(e.message);
                })
                .finally(() => {
                    qmrDispatch(qmrActions.finishCvtChallengeDetailsSave());
                });
        }, SAVE_DELAY_MS);

        return () => clearTimeout(timeoutId);
    }, [
        abortSignalRef,
        hasChanges,
        holdChanges,
        isChallengeDeletionHeld,
        isDraft,
        isEditable,
        isUpdatingDiagnostic,
        qmr?.qmrId,
        qmrDispatch,
    ]);

    /** Loads the CVT challenge reasons once on mount: the Reason's options, and the saved Reason's title. */
    useEffect(() => {
        async function fetchReasons() {
            try {
                const response = await qmrsService.fetchCvtChallengeReasons({ signal: abortSignalRef.current });

                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }

                setReasonOptions(toReasonOptions(response.data.cvtReasons));
            } catch (error) {
                logger.error('Failed to fetch the CVT challenge reasons', { error });
            }
        }

        fetchReasons();
    }, [abortSignalRef]);

    if (!isEditable) {
        if (!cvtChallengeDetails) {
            return null;
        }

        return (
            <div className="d-flex flex-column gap-4">
                <Typography variant="label">{submittedExceptionStatusLabel}</Typography>

                {reasonTitle && (
                    <div className="d-flex flex-column gap-2">
                        <Typography variant="label">
                            {t('qmr:sections.failCode.cvtDiagnostic.challenge.reason.label', 'Reason')}
                        </Typography>
                        <Typography>{reasonTitle}</Typography>
                    </div>
                )}

                {cvtChallengeDetails.cvtChallengeExplanation && (
                    <div className="d-flex flex-column gap-2">
                        <Typography variant="label">
                            {t('qmr:sections.failCode.cvtDiagnostic.challenge.description.label', 'Description')}
                        </Typography>
                        <Typography>{cvtChallengeDetails.cvtChallengeExplanation}</Typography>
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            <Form.Check
                checked={isExceptionStatusChecked}
                disabled={isDisabled}
                id="cvt-exception-status"
                label={<Typography>{applicableExceptionStatusLabel}</Typography>}
                name="cvt-exception-status"
                onChange={handleExceptionStatusCheckboxChange}
                style={textInputStyle}
                type="checkbox"
            />
            {isRecommendationChallenged && (
                <>
                    <Typography>
                        {t('qmr:sections.failCode.cvtDiagnostic.challenge.reason.label', 'Reason')}
                        <Typography color="redOne"> *</Typography>
                    </Typography>
                    <Select
                        disabled={isDisabled}
                        errorMessage={reasonError}
                        id="cvt-challenge-reason"
                        onChange={handleReasonSelectChange}
                        options={reasonOptionsWithPlaceholder}
                        required
                        value={cvtChallengeDetails?.cvtChallengeReason || ''}
                    />

                    <TextareaHelper
                        editable={!isDisabled}
                        errorMessage={descriptionError}
                        label={t('qmr:sections.failCode.cvtDiagnostic.challenge.description.label', 'Description')}
                        labelStyle={labelStyle}
                        onBlur={handleDescriptionBlur}
                        onChangeText={handleDescriptionTextChange}
                        placeholder={t(
                            'qmr:sections.failCode.cvtDiagnostic.challenge.description.placeholder',
                            "Explain why the recommendation doesn't apply to this repair"
                        )}
                        required
                        value={description}
                    />
                    <div className="mt-1">
                        <Typography>
                            {t(
                                'qmr:sections.failCode.cvtDiagnostic.challenge.evidence',
                                'If you have supporting evidence, add it in Attachments below.'
                            )}
                        </Typography>
                    </div>
                </>
            )}
        </>
    );
}

function toReasonOptions(cvtReasons: CvtReason[]): SelectOption[] {
    return cvtReasons.map((cvtReason) => ({
        title: cvtReason.cvtReasonDisplayLabel,
        value: cvtReason.cvtReasonCode,
    }));
}
